import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { 
  Globe, 
  Check, 
  AlertCircle, 
  Loader2, 
  ExternalLink,
  Info,
  Copy,
  CheckCircle
} from 'lucide-react';
import { fetchWithAuth, AUTH_API } from '../../../utils/api';

const ConventionSubdomainSettings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [notification, setNotification] = useState(null);
  const [subdomainStatus, setSubdomainStatus] = useState(null);
  const [currentSubdomain, setCurrentSubdomain] = useState('');
  const [copied, setCopied] = useState(false);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm();
  const watchedSubdomain = watch('subdomain', '');

  // Load existing subdomain settings
  useEffect(() => {
    loadSubdomainSettings();
  }, []);

  const loadSubdomainSettings = async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(`${AUTH_API}/convention-subdomain`);
      
      if (response.conventionSubdomain) {
        setCurrentSubdomain(response.conventionSubdomain);
        setValue('subdomain', response.conventionSubdomain);
        setValue('conventionName', response.conventionName || '');
        setValue('description', response.conventionDescription || '');
        setSubdomainStatus('active');
      }
    } catch (error) {
      console.error('Error loading subdomain settings:', error);
      if (error.message !== 'No subdomain configured') {
        showNotification('error', 'Failed to load subdomain settings');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const checkSubdomainAvailability = async (subdomain) => {
    if (!subdomain || subdomain.length < 3) return;
    
    try {
      setIsChecking(true);
      const response = await fetchWithAuth(`${AUTH_API}/check-subdomain-availability`, {
        method: 'POST',
        body: { subdomain }
      });
      
      setSubdomainStatus(response.available ? 'available' : 'taken');
    } catch (error) {
      console.error('Error checking subdomain:', error);
      setSubdomainStatus('error');
    } finally {
      setIsChecking(false);
    }
  };

  // Debounced subdomain checking
  useEffect(() => {
    const timer = setTimeout(() => {
      if (watchedSubdomain && watchedSubdomain !== currentSubdomain) {
        checkSubdomainAvailability(watchedSubdomain);
      } else if (watchedSubdomain === currentSubdomain) {
        setSubdomainStatus('current');
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [watchedSubdomain, currentSubdomain]);

  const onSubmit = async (data) => {
    try {
      setIsSaving(true);
      
      const response = await fetchWithAuth(`${AUTH_API}/register-convention-subdomain`, {
        method: 'POST',
        body: { 
          subdomain: data.subdomain,
          conventionName: data.conventionName,
          description: data.description
        }
      });

      setCurrentSubdomain(data.subdomain);
      setSubdomainStatus('active');
      showNotification('success', 'Convention subdomain registered successfully!');
      
    } catch (error) {
      console.error('Error registering subdomain:', error);
      showNotification('error', error.message || 'Failed to register subdomain');
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getSubdomainStatusIcon = () => {
    if (isChecking) return <Loader2 className="w-4 h-4 animate-spin text-gray-400" />;
    
    switch (subdomainStatus) {
      case 'available':
        return <Check className="w-4 h-4 text-green-500" />;
      case 'taken':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'active':
      case 'current':
        return <CheckCircle className="w-4 h-4 text-blue-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      default:
        return null;
    }
  };

  const getSubdomainStatusMessage = () => {
    if (isChecking) return 'Checking availability...';
    
    switch (subdomainStatus) {
      case 'available':
        return 'Subdomain is available!';
      case 'taken':
        return 'Subdomain is already taken';
      case 'active':
        return 'Your active subdomain';
      case 'current':
        return 'Current subdomain (no changes)';
      case 'error':
        return 'Error checking availability';
      default:
        return '';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Globe className="w-8 h-8 text-primary" />
          Convention Subdomain
        </h1>
        <p className="text-base-content/70">
          Register your convention's subdomain to create a branded entry point for your participants
        </p>
      </div>

      {/* Current Subdomain Display */}
      {currentSubdomain && (
        <div className="card bg-base-200 shadow-lg">
          <div className="card-body">
            <h2 className="card-title text-success">
              <CheckCircle className="w-5 h-5" />
              Your Convention Subdomain
            </h2>
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-base-100 rounded-lg">
                <code className="text-lg font-mono text-primary">
                  {currentSubdomain}.dance.digital
                </code>
                <button
                  onClick={() => copyToClipboard(`${currentSubdomain}.dance.digital`)}
                  className="btn btn-ghost btn-sm"
                  title="Copy to clipboard"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
                <a
                  href={`https://${currentSubdomain}.dance.digital`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost btn-sm"
                  title="Visit subdomain"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              <div className="alert alert-info">
                <Info className="w-5 h-5" />
                <div>
                  <p className="font-semibold">How it works:</p>
                  <ul className="list-disc list-inside text-sm mt-1 space-y-1">
                    <li>Share this URL with your participants for easy access</li>
                    <li>When they visit, they'll be redirected to dance.digital with access to your events</li>
                    <li>This creates a seamless, branded experience for your convention</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Registration Form */}
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body">
          <h2 className="card-title">
            {currentSubdomain ? 'Update Subdomain' : 'Register Subdomain'}
          </h2>
          
          {notification && (
            <div className={`alert ${notification.type === 'error' ? 'alert-error' : 'alert-success'}`}>
              <AlertCircle className="w-5 h-5" />
              <span>{notification.message}</span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Subdomain Input */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Convention Subdomain *</span>
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="yourconvention"
                    className={`input input-bordered w-full pr-10 ${
                      errors.subdomain ? 'input-error' : ''
                    }`}
                    {...register('subdomain', {
                      required: 'Subdomain is required',
                      pattern: {
                        value: /^[a-zA-Z0-9-]{3,50}$/,
                        message: 'Must be 3-50 characters, letters, numbers, and hyphens only'
                      }
                    })}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {getSubdomainStatusIcon()}
                  </div>
                </div>
                <span className="text-base-content/70">.dance.digital</span>
              </div>
              
              {/* Status Message */}
              {subdomainStatus && (
                <label className="label">
                  <span className={`label-text-alt ${
                    subdomainStatus === 'available' ? 'text-success' :
                    subdomainStatus === 'taken' ? 'text-error' :
                    subdomainStatus === 'active' || subdomainStatus === 'current' ? 'text-info' :
                    'text-warning'
                  }`}>
                    {getSubdomainStatusMessage()}
                  </span>
                </label>
              )}
              
              {errors.subdomain && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.subdomain.message}</span>
                </label>
              )}
            </div>

            {/* Convention Name */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Convention Name *</span>
              </label>
              <input
                type="text"
                placeholder="The Streetz Dance Convention"
                className={`input input-bordered ${errors.conventionName ? 'input-error' : ''}`}
                {...register('conventionName', {
                  required: 'Convention name is required'
                })}
              />
              {errors.conventionName && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.conventionName.message}</span>
                </label>
              )}
            </div>

            {/* Description */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Description</span>
              </label>
              <textarea
                placeholder="Brief description of your convention..."
                className="textarea textarea-bordered h-24"
                {...register('description')}
              />
            </div>

            {/* Submit Button */}
            <div className="form-control">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  isSaving || 
                  isChecking || 
                  subdomainStatus === 'taken' || 
                  subdomainStatus === 'error' ||
                  (subdomainStatus === 'current' && !watchedSubdomain)
                }
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {currentSubdomain ? 'Updating...' : 'Registering...'}
                  </>
                ) : (
                  <>
                    <Globe className="w-4 h-4 mr-2" />
                    {currentSubdomain ? 'Update Subdomain' : 'Register Subdomain'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Information Card */}
      <div className="card bg-base-200 shadow-lg">
        <div className="card-body">
          <h3 className="card-title text-info">
            <Info className="w-5 h-5" />
            How Convention Subdomains Work
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-content flex items-center justify-center text-xs font-bold">1</div>
              <div>
                <p className="font-semibold">Register Your Subdomain</p>
                <p className="text-base-content/70">Choose a unique subdomain that represents your convention</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-content flex items-center justify-center text-xs font-bold">2</div>
              <div>
                <p className="font-semibold">Share with Participants</p>
                <p className="text-base-content/70">Give participants your branded URL (e.g., streetz.dance.digital)</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-content flex items-center justify-center text-xs font-bold">3</div>
              <div>
                <p className="font-semibold">Automatic Access</p>
                <p className="text-base-content/70">When they visit, they automatically get access to your convention's events</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-content flex items-center justify-center text-xs font-bold">4</div>
              <div>
                <p className="font-semibold">Seamless Experience</p>
                <p className="text-base-content/70">They're redirected to dance.digital with only your events visible</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConventionSubdomainSettings; 